import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import AddressMap from "@/components/AddressMap";
import { subLabel, catLabel } from "@/lib/services";
import {
  ArrowRight, MapPin, Phone, Mail, Globe, MessageCircle, Star, Verified,
  Award, Clock, Languages, Briefcase, Heart, Share2, Send, Loader2, Map, Flag
} from "lucide-react";
import { toast } from "sonner";

export default function ProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [review, setReview] = useState({ rating: 5, title: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [chatMsg, setChatMsg] = useState("");

  const load = async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/providers/${id}`),
        axios.get(`${API}/providers/${id}/reviews`)
      ]);
      setData(p.data); setReviews(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
    if (token) {
      axios.get(`${API}/providers/favorites/ids`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setSaved(res.data.ids.includes(id))).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (loading) return <DashboardLayout title=""><div className="glass-card rounded-2xl h-96 skeleton" /></DashboardLayout>;
  if (!data) return <DashboardLayout title=""><div className="glass-card rounded-2xl p-12 text-center text-slate-400">{t("غير موجود", "Not found")}</div></DashboardLayout>;

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${data.business_name || data.full_name}, ${data.city}, ${data.state} ${data.zip_code}`)}`;
  const waUrl = data.whatsapp ? `https://wa.me/${data.whatsapp.replace(/\D/g, "")}` : null;

  const toggleSave = async () => {
    if (!token) { navigate("/login"); return; }
    try {
      if (saved) {
        await axios.delete(`${API}/providers/${id}/favorite`, { headers: { Authorization: `Bearer ${token}` } });
        setSaved(false); toast.success(t("تم الحذف", "Removed"));
      } else {
        await axios.post(`${API}/providers/${id}/favorite`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setSaved(true); toast.success(t("تم الحفظ", "Saved"));
      }
    } catch { toast.error(t("فشل", "Failed")); }
  };

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: data.full_name, url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success(t("تم نسخ الرابط", "Link copied")); }
  };

  const submitReview = async () => {
    if (!review.comment) { toast.error(t("اكتب تعليقاً", "Please add a comment")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/providers/${id}/reviews`,
        { provider_id: id, rating: review.rating, title: review.title, comment: review.comment },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("شكراً لتقييمك!", "Thanks for your review!"));
      setShowReview(false); setReview({ rating: 5, title: "", comment: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل", "Failed")); }
    finally { setSubmitting(false); }
  };

  const sendChat = async () => {
    if (!chatMsg.trim() || !token) return;
    try {
      const res = await axios.post(`${API}/chat/messages`, {
        recipient_id: data.owner_id, content: chatMsg,
        listing_id: data.provider_id, listing_type: "provider",
        listing_title: data.business_name || data.full_name
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الإرسال", "Sent"));
      navigate(`/chat/conversation/${res.data.conversation_id}`);
    } catch (e) { toast.error(t("فشل", "Failed")); }
  };

  const report = async (rid) => {
    try {
      await axios.post(`${API}/reviews/${rid}/report`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الإبلاغ", "Reported"));
    } catch { toast.error(t("فشل", "Failed")); }
  };

  return (
    <DashboardLayout title={data.full_name}>
      <div className="max-w-6xl space-y-6" data-testid="provider-detail">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-yellow-500 hover:underline">
          <ArrowRight size={16} /> {t("رجوع", "Back")}
        </button>

        {/* Cover + profile */}
        <div className="glass-card rounded-2xl overflow-hidden purple-gold-glow">
          <div className="relative h-48 bg-slate-900">
            <ListingImage path={data.cover_photo} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 flex gap-2">
              <button onClick={toggleSave} className={`w-10 h-10 rounded-full backdrop-blur-md ${saved ? "bg-yellow-500 text-black" : "bg-black/60 text-white"} flex items-center justify-center`} data-testid="detail-fav-btn">
                <Heart size={16} fill={saved ? "currentColor" : "none"} />
              </button>
              <button onClick={share} className="w-10 h-10 rounded-full backdrop-blur-md bg-black/60 text-white flex items-center justify-center" data-testid="detail-share-btn">
                <Share2 size={16} />
              </button>
            </div>
          </div>
          <div className="px-6 pb-6 -mt-16 relative">
            <div className="flex items-end gap-4 mb-4 flex-wrap">
              <div className="w-32 h-32 rounded-2xl bg-slate-800 border-4 border-slate-950 overflow-hidden flex-shrink-0">
                <ListingImage path={data.profile_photo} alt={data.full_name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-2xl font-bold text-white">{data.full_name}</h1>
                  {data.verified && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40"><Verified size={12} className="ml-1" />{t("موثّق", "Verified")}</Badge>}
                  {data.top_rated && <Badge className="bg-yellow-500/20 text-yellow-400"><Award size={12} className="ml-1" />{t("Top Rated", "Top Rated")}</Badge>}
                  {data.featured && <Badge className="bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0">{t("مميز", "Featured")}</Badge>}
                </div>
                {data.business_name && <p className="text-slate-400 mb-1">{data.business_name}</p>}
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className="bg-yellow-500/20 text-yellow-400">{subLabel(data.profession, language)}</Badge>
                  <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{data.city}, {data.state} · {data.zip_code}</span>
                  {data.rating !== null && (
                    <span className="text-yellow-400 flex items-center gap-1 text-sm font-bold">
                      <Star size={14} fill="currentColor" /> {data.rating} <span className="text-slate-400 font-normal">({data.review_count} {t("تقييم", "reviews")})</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-slate-300 leading-relaxed">{data.description}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Quick stats */}
            <div className="grid sm:grid-cols-3 gap-3">
              {data.years_experience && (
                <div className="glass-card rounded-xl p-4">
                  <Briefcase className="text-yellow-500 mb-1" size={18} />
                  <div className="text-2xl font-bold text-white">{data.years_experience}+</div>
                  <div className="text-xs text-slate-400">{t("سنوات خبرة", "Years exp.")}</div>
                </div>
              )}
              {data.business_hours && (
                <div className="glass-card rounded-xl p-4">
                  <Clock className="text-yellow-500 mb-1" size={18} />
                  <div className="text-sm font-bold text-white">{data.business_hours}</div>
                  <div className="text-xs text-slate-400">{t("ساعات العمل", "Hours")}</div>
                </div>
              )}
              {data.languages?.length > 0 && (
                <div className="glass-card rounded-xl p-4">
                  <Languages className="text-yellow-500 mb-1" size={18} />
                  <div className="text-sm font-bold text-white">{data.languages.join(", ")}</div>
                  <div className="text-xs text-slate-400">{t("اللغات", "Languages")}</div>
                </div>
              )}
            </div>

            {/* Portfolio photos */}
            {data.portfolio_photos?.length > 0 && (
              <div className="glass-card rounded-2xl p-5">
                <h3 className="font-bold text-white mb-3">{t("المعرض", "Portfolio")}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.portfolio_photos.map((p, i) => (
                    <div key={i} className="aspect-square rounded-xl overflow-hidden bg-slate-900">
                      <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            {/* Reviews */}
            <div className="glass-card rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <MapPin className="text-yellow-500" size={18} />
                {t("الموقع", "Location")}
              </h3>
              <AddressMap address={`${data.business_name || data.full_name}, ${data.city}, ${data.state} ${data.zip_code}`} title={data.business_name || data.full_name} height="h-56" />
            </div>

            <div className="glass-card rounded-2xl p-5 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-white">{t("التقييمات", "Reviews")} ({reviews.length})</h3>
                {user && user.user_id !== data.owner_id && (
                  <Button onClick={() => setShowReview(!showReview)} variant="outline" className="border-slate-700 text-white" data-testid="add-review-btn">
                    {t("اكتب تقييماً", "Write review")}
                  </Button>
                )}
              </div>
              {showReview && (
                <div className="bg-slate-900 rounded-xl p-4 space-y-3" data-testid="review-form">
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} onClick={() => setReview({...review, rating: n})}>
                        <Star size={24} className={n <= review.rating ? "text-yellow-400" : "text-slate-600"} fill={n <= review.rating ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                  <input className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" placeholder={t("عنوان (اختياري)", "Title (optional)")} value={review.title} onChange={(e) => setReview({...review, title: e.target.value})} />
                  <textarea className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" rows={3} placeholder={t("تعليقك...", "Your comment...")} value={review.comment} onChange={(e) => setReview({...review, comment: e.target.value})} data-testid="review-comment" />
                  <Button onClick={submitReview} disabled={submitting} className="btn-primary" data-testid="submit-review-btn">
                    {submitting ? <Loader2 className="animate-spin" size={14} /> : t("إرسال", "Submit")}
                  </Button>
                </div>
              )}
              {reviews.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-6">{t("لا توجد تقييمات بعد. كن أول من يقيّم!", "No reviews yet. Be the first!")}</p>
              ) : (
                <div className="space-y-3">
                  {reviews.map((r) => (
                    <div key={r.review_id} className="bg-slate-900 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="font-semibold text-white text-sm">{r.user_name}</p>
                          <div className="flex">
                            {[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "text-yellow-400" : "text-slate-600"} fill={n <= r.rating ? "currentColor" : "none"} />)}
                          </div>
                        </div>
                        <button onClick={() => report(r.review_id)} className="text-slate-500 hover:text-red-400 text-xs" data-testid={`report-${r.review_id}`}>
                          <Flag size={12} />
                        </button>
                      </div>
                      {r.title && <p className="text-white font-semibold text-sm mb-1">{r.title}</p>}
                      <p className="text-slate-300 text-sm">{r.comment}</p>
                      <p className="text-xs text-slate-500 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact sidebar */}
          <div className="space-y-3">
            <div className="glass-card rounded-2xl p-5 space-y-3 sticky top-4">
              <h3 className="font-bold text-white">{t("تواصل الآن", "Contact now")}</h3>
              <a href={`tel:${data.phone}`} className="block">
                <Button className="w-full btn-primary" data-testid="call-btn"><Phone size={16} className="ml-2" />{data.phone}</Button>
              </a>
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noreferrer" className="block">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="whatsapp-btn">
                    <MessageCircle size={16} className="ml-2" /> WhatsApp
                  </Button>
                </a>
              )}
              {user && user.user_id !== data.owner_id && (
                <>
                  <textarea className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm" rows={2} placeholder={t("رسالة مباشرة...", "Direct message...")} value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} data-testid="provider-chat-input" />
                  <Button onClick={sendChat} disabled={!chatMsg.trim()} variant="outline" className="w-full border-purple-500/40 text-purple-300" data-testid="provider-send-msg-btn">
                    <Send size={14} className="ml-2" />{t("أرسل رسالة", "Send message")}
                  </Button>
                </>
              )}
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="block">
                <Button variant="outline" className="w-full border-slate-700 text-white"><Map size={14} className="ml-2" />{t("الاتجاهات", "Directions")}</Button>
              </a>
              {data.email && (
                <a href={`mailto:${data.email}`} className="block">
                  <Button variant="outline" className="w-full border-slate-700 text-white"><Mail size={14} className="ml-2" />{data.email}</Button>
                </a>
              )}
              {data.website && (
                <a href={data.website} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full border-slate-700 text-white"><Globe size={14} className="ml-2" />{t("الموقع", "Website")}</Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
